/**
 * Date Range section of the PO / SO Headers filter panel: quick presets on the
 * left, From / To date inputs on the right. Used by both views; the supplier
 * view renders the compact variant it always had.
 */
import { Calendar, CalendarRange } from 'lucide-react';
import { FilterSection, FilterField, filterControlClass } from '../../../filters/FilterSection';
import { DATE_RANGE_PRESETS } from '../../../filters/dateRangePresets';
import { cn } from '../../../../lib/utils';

interface POHeaderDateRangeSectionProps {
  dateFrom: string;
  dateTo: string;
  activePreset: string | null;
  onPreset: (key: string) => void;
  onFilterChange: (key: string, value: string) => void;
  /** Supplier view: smaller preset chips and tighter spacing. */
  compact?: boolean;
}

export function POHeaderDateRangeSection({
  dateFrom,
  dateTo,
  activePreset,
  onPreset,
  onFilterChange,
  compact = false,
}: POHeaderDateRangeSectionProps) {
  return (
    <FilterSection title="Date Range" icon={compact ? Calendar : CalendarRange}>
      <div className={cn('flex flex-wrap items-start justify-between', compact ? 'gap-4' : 'gap-6')}>
        {/* Quick date range presets */}
        <div className={cn('flex flex-wrap gap-2', compact ? 'pb-1' : 'max-w-2xl')}>
          {DATE_RANGE_PRESETS.map((preset) => (
            <button
              key={preset.key}
              type="button"
              aria-pressed={activePreset === preset.key}
              onClick={() => onPreset(preset.key)}
              className={cn(
                'font-medium border rounded-md transition-colors',
                compact ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm',
                activePreset === preset.key
                  ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                  : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary',
              )}
            >
              {preset.label}
            </button>
          ))}
        </div>
        {/* Date From / Date To inputs */}
        <div className={cn('flex gap-4', !compact && 'flex-wrap')}>
          <div className="w-44">
            <FilterField label="From Date">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => onFilterChange('dateFrom', e.target.value)}
                className={filterControlClass}
              />
            </FilterField>
          </div>
          <div className="w-44">
            <FilterField label="To Date">
              <input
                type="date"
                value={dateTo}
                onChange={(e) => onFilterChange('dateTo', e.target.value)}
                className={filterControlClass}
              />
            </FilterField>
          </div>
        </div>
      </div>
    </FilterSection>
  );
}
